import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { Category } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface CategoryCardProps {
  category: Category;
  onToggleVisibility: (categoryId: string, categoryName: string, currentVisibility: boolean) => void;
  onDelete: (categoryId: string, categoryName: string) => void;
  isToggling?: boolean;
  isDeleting?: boolean;
}

export function CategoryCard({
  category,
  onToggleVisibility,
  onDelete,
  isToggling = false,
  isDeleting = false,
}: CategoryCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const categoryName = currentLanguage === 'ar' ? category.name : category.nameEn;
  const categoryDescription = currentLanguage === 'ar' ? category.description : category.descriptionEn;

  return (
    <div className="card-glass rounded-lg p-4 hover:border-brand-accent/50 transition-all duration-300">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-neutral-light mb-1">
              {categoryName}
            </h3>
            {categoryDescription && (
              <p className="text-sm text-neutral-medium font-body line-clamp-2">
                {categoryDescription}
              </p>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.categories.table.price')}
            </span>
            <span className="text-sm text-neutral-light font-body font-semibold">
              {category.price.toFixed(2)} USD
            </span>
          </div>
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.categories.table.products')}
            </span>
            <span className="text-sm text-neutral-light font-body font-semibold">
              {category.availableProductsCount}
            </span>
          </div>
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.categories.table.status')}
            </span>
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                category.isHidden
                  ? 'bg-danger/20 text-danger'
                  : 'bg-green-500/20 text-green-500'
              }`}
            >
              {category.isHidden
                ? t('admin.categories.status.hidden')
                : t('admin.categories.status.visible')}
            </span>
          </div>
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.categories.table.type')}
            </span>
            <span
              className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                category.isParent
                  ? 'bg-brand-accent/20 text-brand-accent'
                  : 'bg-neutral-dark text-neutral-medium'
              }`}
            >
              {category.isParent
                ? t('admin.categories.type.parent')
                : t('admin.categories.type.child')}
            </span>
          </div>
        </div>

        {/* Date */}
        <div>
          <span className="text-xs text-neutral-medium font-body">
            {t('admin.categories.table.created')}: {formatDate(category.createdAt, 'PP h:mm a', currentLanguage)}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-neutral-dark">
          <Link
            to={`/admin/categories/${category._id}/edit`}
            className="flex-1 px-3 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
            title={t('admin.categories.actions.edit')}
          >
            {t('admin.categories.actions.edit')}
          </Link>
          <button
            onClick={() => onToggleVisibility(category._id, categoryName, category.isHidden)}
            disabled={isToggling}
            className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm font-body ${
              category.isHidden
                ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                : 'bg-neutral-dark text-neutral-medium hover:bg-neutral-dark/80'
            }`}
            title={
              category.isHidden
                ? t('admin.categories.actions.show')
                : t('admin.categories.actions.hide')
            }
          >
            {category.isHidden
              ? t('admin.categories.actions.show')
              : t('admin.categories.actions.hide')}
          </button>
          <button
            onClick={() => onDelete(category._id, categoryName)}
            disabled={isDeleting}
            className="flex-1 px-3 py-2 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
            title={t('admin.categories.actions.delete')}
          >
            {t('admin.categories.actions.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
